import React from "react";
import { useEffect, useState, useRef } from "react";
import {
  Button,
  Card,
  Table,
  Col,
  Dropdown
} from "react-bootstrap";
import ExclusionRegisterDialog from "./ExclusionRegisterDialog";
import { useSelector } from 'react-redux';
import { BUTTON_LABELS } from "../../lang/ja";
import FanLoadingIcon from "components/FanLoadingIcon";
import PaginationComponent from "components/PaginationComponent";
import { NUM_ITEMS_PER_PAGE } from "../../constants/table";
import { ThreeDotsVertical, Trash3Fill, PencilSquare } from 'react-bootstrap-icons';
import "./CommonTable.css";
import moment from 'moment';
import {
  listExclusionsSortByDate,
  adminCreateExclusion,
  adminUpdateExclusion,
  adminDeleteExclusion
} from "apis/ExclusionApi";
import { useDialog } from "../../contexts/DialogContext.tsx";

// Table column styles
const tableStyles = {
  urlColumn: {
    width: '75%',
    lineBreak: 'anywhere',
    padding: '12px 16px'
  }
};

function ExclusionTable () {
  /***** States and Variables *****/
  const [editedExclusion, setEditedExclusion] = useState(null);
  const [exclusions,setExclusions] = useState([]);
  const { userInfo } = useSelector((state) => state.users)
  const governmentId = userInfo?.governmentId;
  const nextTokenRef = useRef(null);
  const [curPage, setCurPage] = useState(0);
  const numPages = Math.ceil(exclusions.length/NUM_ITEMS_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const { success, error } = useDialog();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [unfilteredExclusions,setUnfilteredExclusions] = useState([]);

  /***** Processing *****/
  useEffect(()=>{
    const downloadExclusions = async () => {
      //List exclusions sort by date
      const res = await listExclusionsSortByDate(governmentId, null);
      if (res.success) {
        setExclusions(res.data);
        nextTokenRef.current = res.nextToken;
      }
    };
    if (governmentId) downloadExclusions();
  }, [governmentId])

  useEffect(()=>{
    // First time searching - store the full list
    if (searchKeyword && unfilteredExclusions.length === 0) {
      setUnfilteredExclusions(exclusions);
    }

    // Perform search
    if (searchKeyword) {
      const filteredExclusions = unfilteredExclusions.filter(exclusion =>
        exclusion.url.toLowerCase().includes(searchKeyword.toLowerCase())
      );
      setExclusions(filteredExclusions);
    }
    // When search is cleared, restore original list
    else if (unfilteredExclusions.length > 0) {
      setExclusions(unfilteredExclusions);
      setUnfilteredExclusions([]); // Clear backup only after restoring
    }
  }, [searchKeyword, unfilteredExclusions])

  /***** Event Handlers *****/
  const onRegisterExclusion = async (url) => {
    if (loading) return;
    setLoading(true);
    console.log("editedExclusion",editedExclusion)

    //Check if url is empty
    //Check if url is already registered
    const isRegistered = exclusions.some(exclusion => exclusion.url === url);
    if (isRegistered) {
      error("除外ページURLはすでに登録されています");
      setLoading(false);
      return;
    }
    if(editedExclusion.id) {
      //Edit exclusion case
      const input = {
        id: editedExclusion.id,
        governmentId: editedExclusion.governmentId,
        url: url,
        createdAt: editedExclusion.createdAt,
        updatedAt: new Date().toISOString()
      };
      const res = await adminUpdateExclusion(input);
      console.log("res", res)
      if (res.success) {
        const pos = exclusions.indexOf(editedExclusion);
        const updatedExclusions = [...exclusions];
        updatedExclusions.splice(pos, 1, res.data);
        setExclusions(updatedExclusions);

        // Update unfilteredExclusions if there is a search
        if (unfilteredExclusions.length > 0) {
          const unfilteredPos = unfilteredExclusions.findIndex(item => item.id === editedExclusion.id);
          if (unfilteredPos >= 0) {
            const updatedUnfiltered = [...unfilteredExclusions];
            updatedUnfiltered.splice(unfilteredPos, 1, res.data);
            setUnfilteredExclusions(updatedUnfiltered);
          }
        }
        success("除外ページURLを更新しました");
      }
      else {
        const err = res.error.errors[0].message;
        error(err);
      }
    }
    else {
      //New exclusion case
      const input = {
        governmentId,
        url,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const res = await adminCreateExclusion(input);
      if (res.success) {
        const newData = res.data;
        setExclusions(prev => [newData, ...prev]);

        // Update unfilteredExclusions if there is a search
        if (unfilteredExclusions.length > 0) {
          setUnfilteredExclusions(prev => [newData, ...prev]);
        }
        success("除外ページURLを登録しました");
      }
      else {
        const err = res.error.errors[0].message;
        error(err);
      }
    }
    setEditedExclusion(null);
    setLoading(false);
  }

  const onPageChange = async (page) => {
    setCurPage(page);
    if (page+1===numPages) {
      const nextToken = nextTokenRef.current;
      if (nextToken) {
        //List exclusions sort by date
        const res = await listExclusionsSortByDate(governmentId, nextToken);
        if (res.success) {
          setExclusions([...exclusions, ...res.data]);
          nextTokenRef.current = res.nextToken;
        }
      }
    }
  }
  const onDeleteExclusion = async (exclusion) => {
    if (loading) return;
    setLoading(true);
    const input = {
      governmentId: exclusion.governmentId,
      createdAt: exclusion.createdAt
    };
    const res = await adminDeleteExclusion(input);
    if (res.success) {
      // Update main exclusions list
      setExclusions(prev => prev.filter(item => item.id !== exclusion.id));

      // Update unfilteredExclusions if there is a search
      if (unfilteredExclusions.length > 0) {
        setUnfilteredExclusions(prev => prev.filter(item => item.id !== exclusion.id));
      }
      success("除外ページURLを削除しました");
    }
    else {
      const err = res.error.errors[0].message;
      error(err);
    }
    setLoading(false);
  }
  /***** Sub Components *****/
  /***** Main Render *****/
  return <>
    <Col md="12">
      <Card className="strpied-tabled-with-hover">
        <Card.Header>
          <Card.Title as="h4">
            除外ページURL一覧
          </Card.Title>
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              className="form-control"
              placeholder="除外ページURLを入力"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              style={{ width: "300px", marginRight: "8px" , marginTop: "10px"}}
            />
          </div>
          <div className="d-flex flex-row justify-content-between align-items-end">
            <PaginationComponent
              currentPage={curPage}
              totalItems={exclusions.length}
              onPageChange={onPageChange}/>
            <Button
              className="btn-outline btn-wd mr-1"
              variant="default"
              onClick={()=>{setEditedExclusion({})}}>
              {BUTTON_LABELS.BUTTON_REGISTER_NEW}
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="table-responsive p-0">
          <Table className="table-hover w-full">
            <thead>
              <tr>
                <th style={tableStyles.urlColumn}>URL</th>
                <th style={tableStyles.urlColumn}>作成日時</th>
                <th style={{ width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {exclusions.length > 0 ? (
                exclusions.map((exclusion, i) => {
                  if (i < curPage * NUM_ITEMS_PER_PAGE || i >= (curPage + 1) * NUM_ITEMS_PER_PAGE) return null;
                  return (
                    <tr key={exclusion.id+i}>
                      <td style={tableStyles.urlColumn}>
                        {exclusion.url}
                      </td>
                      <td style={tableStyles.urlColumn}>
                        {moment(exclusion.createdAt).format("yyyy/MM/DD HH:mm")}
                      </td>
                      <td style={{ width: '40px' }}>
                        <Dropdown align="end" drop="down">
                          <Dropdown.Toggle
                            variant="link"
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              border: 'none',
                              background: 'transparent',
                              color: '#595959',
                              padding: '4px',
                              borderRadius: '4px',
                            }}
                          >
                            <ThreeDotsVertical size={16} />
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            style={{
                              minWidth: '120px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              border: '1px solid #f0f0f0'
                            }}
                          >
                            {/**** EDIT OR DETAIL ****/}
                            <Dropdown.Item
                              onClick={() => setEditedExclusion(exclusion)}
                              className="d-flex align-items-center"
                              style={{
                                padding: '8px 12px',
                                fontSize: '14px'
                              }}
                            >
                              <PencilSquare size={16} style={{ marginRight: '8px' }}/>
                              {BUTTON_LABELS.BUTTON_EDIT}
                            </Dropdown.Item>
                            {/**** DELETE ****/}
                            <Dropdown.Item
                              onClick={() => onDeleteExclusion(exclusion)}
                              className="d-flex align-items-center text-danger"
                              style={{
                                padding: '8px 12px',
                                fontSize: '14px'
                              }}
                            >
                              <Trash3Fill size={16} style={{ marginRight: '8px' }}/>
                              {BUTTON_LABELS.BUTTON_DELETE}
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="text-center py-3" colSpan={3} style={{display: 'table-cell'}}>除外ページURLが登録されていません</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Col>
    <ExclusionRegisterDialog
      isOpen={!!editedExclusion}
      isNew={!editedExclusion?.id}
      onClose={()=>{setEditedExclusion(null)}}
      onRegister={onRegisterExclusion}
      url={editedExclusion?.url}
      editable={true}
    />
    {loading && <FanLoadingIcon size={80}/>}
  </>
}

export default ExclusionTable;
